import type { ISODate, YearMonth } from './contract.js';
import { geo } from './tokens.js';

/** Real-date counterpart of the prototype's day numbers (03 §2). Days are ISO dates, never timestamps. */

export const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'] as const;
/** Week starts on Monday. */
export const WEEKDAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'] as const;

const DAY_MS = 86_400_000;
const pad = (n: number) => String(n).padStart(2, '0');

interface Parts { readonly year: number; readonly month: number; readonly day: number }

export function partsOf(date: ISODate): Parts {
  const [y, m, d] = date.split('-').map(Number);
  return { year: y ?? 1970, month: m ?? 1, day: d ?? 1 };
}
const utc = (date: ISODate) => { const p = partsOf(date); return Date.UTC(p.year, p.month - 1, p.day); };
export const isoOf = (year: number, month: number, day: number): ISODate => `${String(year).padStart(4, '0')}-${pad(month)}-${pad(day)}`;

export function addDays(date: ISODate, days: number): ISODate {
  const t = new Date(utc(date) + days * DAY_MS);
  return isoOf(t.getUTCFullYear(), t.getUTCMonth() + 1, t.getUTCDate());
}
/** Whole days from `from` to `to` (positive when `to` is later). */
export const daysBetween = (from: ISODate, to: ISODate): number => Math.round((utc(to) - utc(from)) / DAY_MS);
/** ISO dates compare as strings. */
export const compareDates = (a: ISODate, b: ISODate): number => (a < b ? -1 : a > b ? 1 : 0);

/** Monday = 0 … Sunday = 6. */
export const weekdayIndex = (date: ISODate): number => (new Date(utc(date)).getUTCDay() + 6) % 7;
export const daysInMonth = (ym: YearMonth): number => new Date(Date.UTC(ym.year, ym.month, 0)).getUTCDate();

export const monthOf = (date: ISODate): YearMonth => { const p = partsOf(date); return { year: p.year, month: p.month }; };
export const monthKey = (ym: YearMonth): string => `${String(ym.year).padStart(4, '0')}-${pad(ym.month)}`;
export const monthFromKey = (key: string): YearMonth => { const [y, m] = key.split('-').map(Number); return { year: y ?? 1970, month: m ?? 1 }; };
export const compareMonths = (a: YearMonth, b: YearMonth): number => (a.year - b.year) * 12 + (a.month - b.month);
export function addMonths(ym: YearMonth, n: number): YearMonth {
  const index = ym.year * 12 + (ym.month - 1) + n;
  return { year: Math.floor(index / 12), month: (index % 12 + 12) % 12 + 1 };
}
export const firstOfMonth = (ym: YearMonth): ISODate => isoOf(ym.year, ym.month, 1);
export const monthName = (ym: YearMonth): string => MONTH_NAMES[ym.month - 1] ?? '';

/** The prototype's `months[i]` record: the 42-cell grid of a month. `lead` = empty cells before the 1st. */
export function monthGrid(ym: YearMonth): { readonly name: string; readonly days: number; readonly lead: number } {
  return { name: monthName(ym), days: daysInMonth(ym), lead: weekdayIndex(firstOfMonth(ym)) };
}

/** Cell index (0–41) of a date in its own month's grid, and its page position (`gridPos`). */
export function gridPos(date: ISODate): { readonly x: number; readonly y: number; readonly i: number } {
  const i = monthGrid(monthOf(date)).lead + partsOf(date).day - 1;
  return { x: geo.gridX + (i % 7) * geo.step, y: geo.gridY + Math.floor(i / 7) * geo.step, i };
}

/** "Thursday 1 October": no year, no leading zero (`dateLong`). */
export function dateLong(date: ISODate): string {
  const p = partsOf(date);
  return `${WEEKDAY_NAMES[weekdayIndex(date)]} ${p.day} ${MONTH_NAMES[p.month - 1]}`;
}
/** "Thu 1 Oct": first three letters of each name (`dateShort`). */
export function dateShort(date: ISODate): string {
  const p = partsOf(date);
  return `${(WEEKDAY_NAMES[weekdayIndex(date)] ?? '').slice(0, 3)} ${p.day} ${(MONTH_NAMES[p.month - 1] ?? '').slice(0, 3)}`;
}

/**
 * The prototype's day number `n` (0 = 1 August 2026). Only used to seed the deterministic shuffles,
 * so a given date pops in the same order as in the prototype; any date maps to a stable integer.
 */
export const dayNumber = (date: ISODate): number => daysBetween('2026-08-01', date);
